'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import { Link } from '@packages/modules/src/Link/Link';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { CategoryRecommendationFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryRecommendationFragmentFragmentDoc';
import { Card } from '@packages/shared/src/ui/Card/Card';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { getClickTrackingBrandsCategoriesURLExtension } from '../../helpers/getClickTrackingBrandsCategoriesURLExtension';
import type { PageTemplate, RdeType } from '../../types';

/* GraphQL */ `
  fragment CategoryRecommendationFragment on CategoryRecommendation {
    category {
      categoryId
      categoryLink
      name
      topsellerProduct {
        imageUrl
        productId
      }
    }
    tracking {
      token
      logic
    }
  }
`;

export type RecoCategoryCardProps = {
  /**
   * number of brands
   */
  categoriesLength: number;
  /**
   * pageTemplate for products tracking
   */
  pageTemplate: PageTemplate;
  /**
   * position of product
   */
  position: number;
  recommendationData: FragmentType<typeof CategoryRecommendationFragmentFragmentDoc>;
  /**
   * Recommendation type
   */
  rdeType: RdeType;
};

export const RecoCategoryCard = ({
  categoriesLength,
  recommendationData,
  pageTemplate,
  position,
  rdeType,
}: RecoCategoryCardProps) => {
  const dispatchGTMEvent = useTracking();

  const {
    category: { name, topsellerProduct, categoryLink, categoryId },
    tracking,
  } = unmask<typeof CategoryRecommendationFragmentFragmentDoc>(recommendationData);

  return (
    <Card className="h-full w-full">
      <Link
        href={`${categoryLink}${categoryLink.endsWith('/') ? '' : '/'}?highlight=${
          topsellerProduct.productId
        }${getClickTrackingBrandsCategoriesURLExtension({
          cid: categoryId,
          itemsToLoad: categoriesLength,
          pageTemplate,
          recoLogic: tracking.logic,
          rdeType,
          name,
          position: position + 1,
        })}`}
        className="hover:text-color-primary no-underline hover:no-underline"
        onClick={() => {
          dispatchGTMEvent({
            event: 'ga_event',
            eventAction: 'click',
            eventCategory: 'recoClick',
            eventLabel: 'categoryRecoItemClick',
            eventValue: {
              trackingToken: tracking.token,
              pid: topsellerProduct.productId,
            },
          });
        }}
      >
        <div className="flex flex-col justify-center">
          <div className="relative px-0.5 pt-0.5">
            <Image
              src={`${topsellerProduct.imageUrl}?$PL_square_hq$`}
              width={300}
              height={300}
              // TODO: add alt text to data structure
              alt={`Image of category: ${name}`}
            />
          </div>
          <div className="flex min-h-4.5 w-full items-center justify-center">
            <p title={name} className="text-size-body-sm px-1.5 text-center">
              {name}
            </p>
          </div>
        </div>
      </Link>
    </Card>
  );
};
